import { useState } from 'react';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  GoodsReceiptDetailSchema,
  PurchaseOrderDetailSchema,
  type PurchaseOrderDetailDto,
} from '@taqon/contracts';
import { Link as RouterLink, useNavigate, useParams } from 'react-router';
import { errorMessage, getData, newIdempotencyKey, postJson } from '../../shared/api/client';
import { RecordHistory } from '../../shared/audit/AuditTrail';
import { DocumentsPanel } from '../../shared/documents/DocumentsPanel';
import { hasCapability, useSession } from '../../shared/session/session';
import { ConfirmDialog } from '../../shared/ui/confirm';
import { FormDialog, StatusChip } from '../../shared/ui/forms';
import { EmptyState, LoadingState, PageHeader, Section } from '../../shared/ui/page';
import { PersonName } from '../../shared/ui/person';
import { ApprovalPanel, useSubmittedByMe } from '../../shared/workflow/ApprovalPanel';
import { Facts, RecordFailure } from '../finance/detail';
import {
  dimensionText,
  EDITABLE,
  GR_API,
  GR_TYPE,
  OriginChip,
  PO_API,
  PO_TYPE,
  PR_TYPE,
  PurchasingLink,
  useBusinessToday,
} from './purchasing';

type Action = 'submit' | 'withdraw' | 'issue' | 'cancel' | 'close';

const ACTION_TEXT: Readonly<Record<Action, { title: string; message: string; label: string }>> = {
  submit: {
    title: 'Submit for approval',
    message: 'The order goes to its configured approval route and cannot be edited meanwhile.',
    label: 'Submit',
  },
  withdraw: {
    title: 'Withdraw from approval',
    message:
      'The order leaves approval and goes back to draft. Its approvers are no longer asked to ' +
      'decide it; you can correct and resubmit it, or cancel it.',
    label: 'Withdraw',
  },
  issue: {
    title: 'Issue to the supplier',
    message: 'Issuing records today and you as the issuer. Goods can then be received against it.',
    label: 'Issue',
  },
  cancel: {
    title: 'Cancel the purchase order',
    message:
      'A cancelled order cannot be issued or received, and its quantities no longer count ' +
      'against its purchase request.',
    label: 'Cancel order',
  },
  close: {
    title: 'Close the purchase order',
    message: 'Nothing more can be received against a closed order.',
    label: 'Close order',
  },
};

/**
 * One purchase order (`/procurement/purchase-orders/:id`; FRD-PRC-027 to 034): its origin, supplier
 * and terms, lines with ordered, received and outstanding quantity and value, the request it
 * converts and its receipts, its approvals, attachments and history.
 */
export function PurchaseOrderDetailPage() {
  const { id = '' } = useParams();
  const session = useSession();
  const queryClient = useQueryClient();
  const can = (capability: string) => hasCapability(session.data, capability);
  const [confirming, setConfirming] = useState<Action | null>(null);
  const [receiving, setReceiving] = useState(false);
  const order = useQuery({
    queryKey: ['procurement', 'purchase-order', id],
    queryFn: () => getData(`${PO_API}/${id}`, PurchaseOrderDetailSchema),
  });
  const submittedByMe = useSubmittedByMe(PO_TYPE, id, order.data?.order.status === 'submitted');
  const act = useMutation({
    mutationFn: (action: Action) =>
      postJson(
        `${PO_API}/${id}/${action}`,
        {},
        PurchaseOrderDetailSchema,
        session.data?.csrfToken ?? '',
        { expectedVersion: order.data?.order.version },
      ),
    onSuccess: async () => {
      setConfirming(null);
      await queryClient.invalidateQueries({ queryKey: ['procurement'] });
      await queryClient.invalidateQueries({ queryKey: ['workflow'] });
    },
  });
  const back = (
    <Button component={RouterLink} to="/procurement/purchase-orders" startIcon={<ArrowBackIcon />}>
      All purchase orders
    </Button>
  );
  if (order.isPending) return <LoadingState label="Loading the purchase order" />;
  if (order.isError) {
    return (
      <RecordFailure title="Purchase order" noun="purchase order" error={order.error} back={back} />
    );
  }
  const detail = order.data;
  const o = detail.order;
  const canChange = can(`${PO_TYPE}:change`);
  const editable = EDITABLE.includes(o.status);
  const receivable = o.status === 'issued' || o.status === 'partially_received';
  const cancellable =
    (editable || o.status === 'approved' || o.status === 'issued') && detail.receipts.length === 0;
  const closable = ['issued', 'partially_received', 'received'].includes(o.status);
  const button = (action: Action, variant: 'contained' | 'text' = 'text') => (
    <Button
      variant={variant}
      color={action === 'cancel' ? 'warning' : 'primary'}
      onClick={() => {
        setConfirming(action);
      }}
    >
      {action === 'submit' && o.status !== 'draft' ? 'Resubmit' : ACTION_TEXT[action].label}
    </Button>
  );

  return (
    <Stack spacing={3}>
      <PageHeader
        title={`Purchase order ${o.documentNo}`}
        description={o.supplierLabel ?? o.supplierId}
        actions={
          <>
            {back}
            {canChange && editable && (
              <Button component={RouterLink} to={`/procurement/purchase-orders/${o.id}/edit`}>
                Edit
              </Button>
            )}
            {canChange && editable && button('submit', 'contained')}
            {canChange && submittedByMe && button('withdraw')}
            {canChange && o.status === 'approved' && button('issue', 'contained')}
            {can(`${GR_TYPE}:create`) && receivable && (
              <Button
                variant="contained"
                onClick={() => {
                  setReceiving(true);
                }}
              >
                Record receipt
              </Button>
            )}
            {canChange && closable && button('close')}
            {canChange && cancellable && button('cancel')}
          </>
        }
      />
      <Section title="Order">
        <Facts
          label="Purchase order details"
          facts={[
            ['Status', <StatusChip key="s" status={o.status} />],
            ['Origin', <OriginChip key="o" origin={o.origin} />],
            ...(o.emergencyReason !== null
              ? ([['Bypass reason', o.emergencyReason]] as [string, string][])
              : []),
            [
              'Purchase request',
              detail.purchaseRequest === null ? (
                '—'
              ) : (
                <PurchasingLink key="pr" type={PR_TYPE} id={detail.purchaseRequest.id}>
                  {detail.purchaseRequest.documentNo}
                </PurchasingLink>
              ),
            ],
            [
              'Supplier',
              <PurchasingLink key="sup" type="procurement.supplier" id={o.supplierId}>
                {o.supplierLabel ?? o.supplierId}
              </PurchasingLink>,
            ],
            ['Order date', o.poDate],
            ['Delivery date', o.deliveryDate ?? '—'],
            ['Point of delivery', o.pointOfDelivery ?? '—'],
            ['Contact person', o.contactPerson ?? '—'],
            ['Payment terms', o.paymentTerms ?? '—'],
            ['Terms and conditions', o.termsAndConditions ?? '—'],
            [
              'Issued',
              o.issuedAt === null ? (
                'Not issued'
              ) : (
                <span key="issued">
                  {new Date(o.issuedAt).toLocaleString()} by{' '}
                  {o.issuedByLabel === null || o.issuedBy === null ? (
                    (o.issuedByLabel ?? o.issuedBy ?? '')
                  ) : (
                    <PersonName name={o.issuedByLabel} userId={o.issuedBy} />
                  )}
                </span>
              ),
            ],
            ['Net total (AED)', o.netTotal],
            ['Estimated VAT (AED)', o.vatTotal],
            ['Total incl. VAT (AED)', o.grossTotal],
            ['Received value (AED)', o.receivedValue],
            ['Outstanding value (AED)', o.outstandingValue],
          ]}
        />
      </Section>
      <Section title="Lines">
        <Table size="small" aria-label="Purchase order lines">
          <TableHead>
            <TableRow>
              <TableCell>#</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Request line</TableCell>
              <TableCell>Account</TableCell>
              <TableCell>Dimensions</TableCell>
              <TableCell align="right">Ordered</TableCell>
              <TableCell align="right">Unit price</TableCell>
              <TableCell align="right">Line total</TableCell>
              <TableCell>Tax code</TableCell>
              <TableCell align="right">Est. VAT</TableCell>
              <TableCell align="right">Received</TableCell>
              <TableCell align="right">Outstanding</TableCell>
              <TableCell align="right">Received value</TableCell>
              <TableCell align="right">Outstanding value</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {detail.lines.map((line) => (
              <TableRow key={line.id}>
                <TableCell>{line.lineNo}</TableCell>
                <TableCell>{line.description}</TableCell>
                <TableCell>{line.purchaseRequestLineNo ?? '—'}</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{line.accountLabel ?? '—'}</TableCell>
                <TableCell>{dimensionText(line)}</TableCell>
                <TableCell align="right">{line.quantity}</TableCell>
                <TableCell align="right">{line.unitPrice}</TableCell>
                <TableCell align="right">{line.lineTotal}</TableCell>
                <TableCell>{line.taxCodeLabel ?? '—'}</TableCell>
                <TableCell align="right">{line.estimatedVat}</TableCell>
                <TableCell align="right">{line.receivedQuantity}</TableCell>
                <TableCell align="right">{line.outstandingQuantity}</TableCell>
                <TableCell align="right">{line.receivedValue}</TableCell>
                <TableCell align="right">{line.outstandingValue}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Section>
      <Section title="Receipts">
        {detail.receipts.length === 0 ? (
          <EmptyState title="Nothing received yet" />
        ) : (
          <Table size="small" aria-label="Receipts against this order">
            <TableHead>
              <TableRow>
                <TableCell>Receipt</TableCell>
                <TableCell>Date</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {detail.receipts.map((r) => (
                <TableRow key={r.id}>
                  <TableCell>
                    <PurchasingLink type={GR_TYPE} id={r.id}>
                      {r.documentNo}
                    </PurchasingLink>
                  </TableCell>
                  <TableCell>{r.receiptDate}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Section>
      <ApprovalPanel
        recordType={PO_TYPE}
        recordId={o.id}
        onDecided={() => {
          void queryClient.invalidateQueries({ queryKey: ['procurement'] });
        }}
      />
      <DocumentsPanel recordType={PO_TYPE} recordId={o.id} canAttach={canChange} />
      <RecordHistory recordType={PO_TYPE} recordId={o.id} />
      {confirming !== null && (
        <ConfirmDialog
          open
          title={ACTION_TEXT[confirming].title}
          message={ACTION_TEXT[confirming].message}
          confirmLabel={ACTION_TEXT[confirming].label}
          tone={confirming === 'cancel' ? 'warning' : 'primary'}
          busy={act.isPending}
          error={act.isError ? errorMessage(act.error) : null}
          onCancel={() => {
            act.reset();
            setConfirming(null);
          }}
          onConfirm={() => {
            act.mutate(confirming);
          }}
        />
      )}
      {receiving && (
        <ReceiptDialog
          detail={detail}
          onClose={() => {
            setReceiving(false);
          }}
        />
      )}
    </Stack>
  );
}

const DECIMAL = /^\d{1,15}(\.\d{1,4})?$/;

/**
 * Records a receipt against the order (FRD-PRC-039, PRC-040): the receipt date and, per line
 * still outstanding, the quantity received — partial receipts allowed, never above what is
 * outstanding (the API refuses that too).
 */
export function ReceiptDialog({
  detail,
  onClose,
}: {
  detail: PurchaseOrderDetailDto;
  onClose: () => void;
}) {
  const session = useSession();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const today = useBusinessToday();
  const open = detail.lines.filter((l) => !/^0*(\.0*)?$/.test(l.outstandingQuantity));
  const [receiptDate, setReceiptDate] = useState(today);
  const [quantities, setQuantities] = useState<Record<string, string>>(() =>
    Object.fromEntries(open.map((l) => [l.id, l.outstandingQuantity])),
  );
  const [idempotencyKey] = useState(newIdempotencyKey);
  const lines = open
    .map((l) => ({ purchaseOrderLineId: l.id, quantityReceived: (quantities[l.id] ?? '').trim() }))
    .filter((l) => l.quantityReceived !== '' && !/^0*(\.0*)?$/.test(l.quantityReceived));
  const valid =
    receiptDate !== '' && lines.length > 0 && lines.every((l) => DECIMAL.test(l.quantityReceived));
  const record = useMutation({
    mutationFn: () =>
      postJson(
        GR_API,
        { purchaseOrderId: detail.order.id, receiptDate, lines },
        GoodsReceiptDetailSchema,
        session.data?.csrfToken ?? '',
        { idempotencyKey },
      ),
    onSuccess: async (saved) => {
      await queryClient.invalidateQueries({ queryKey: ['procurement'] });
      onClose();
      await navigate(`/procurement/receipts/${saved.receipt.id}`);
    },
  });
  return (
    <FormDialog
      open
      title={`Record a receipt against ${detail.order.documentNo}`}
      submitLabel="Record receipt"
      canSubmit={valid}
      submitting={record.isPending}
      error={record.isError ? errorMessage(record.error) : null}
      onClose={onClose}
      onSubmit={() => {
        record.mutate();
      }}
    >
      <TextField
        size="small"
        type="date"
        label="Receipt date"
        required
        value={receiptDate}
        slotProps={{ inputLabel: { shrink: true } }}
        onChange={(e) => {
          setReceiptDate(e.target.value);
        }}
      />
      {open.map((line) => (
        <TextField
          key={line.id}
          size="small"
          label={`Line ${String(line.lineNo)}: ${line.description}`}
          helperText={`${line.outstandingQuantity} outstanding. Leave 0 for nothing received.`}
          value={quantities[line.id] ?? ''}
          onChange={(e) => {
            setQuantities((current) => ({ ...current, [line.id]: e.target.value }));
          }}
        />
      ))}
    </FormDialog>
  );
}
